import { HStack, useDisclosure } from "@chakra-ui/react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import useQuery from "../../hooks/useQuery";
import Tab from "../../components/Shared/Tabs/Tab";
import { feedBackTypeTab } from "../../configs/Reports.config";
import useQueryState from "../../hooks/useQueryState";
import { getStartEndFromQuery } from "../../utils/schedule.util";
import AppRangePicker, { DateRange } from "../../components/Core/AppRangePicker/AppRangePicker";
import ConsultantAutoComplete from "../../components/Core/AutoComplete/ConsultantAutoComplete";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import TableLayout from "../../components/Layout/TableLayout";
import { FeedbackReportBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { useConsultantRatings, useDeleteFeedback } from "../../networking/consultant.hooks";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { useOrganisation } from "../../networking/organisation.hooks";
import CustomerAutoComplete from "../../components/Core/AutoComplete/CustomerAutoComplete";
import { useCustomerRatings } from "../../networking/customer.hooks";
import ConsultantFeedbackRow from "../../components/Shared/FeedbackRow/ConsultantFeedback";
import CustomerFeedbackRow from "../../components/Shared/FeedbackRow/CustomerFeedback";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import { ConsultantRating, FeedbackModal } from "../../models/Consultant";
import { CustomerRating } from "../../models/CustomerUser";
import { useState } from "react";
import { getFeedbackData } from "../../utils/feedback.utils";
import FeedbackContent from "../../components/Shared/FeedbackRow/FeedbackContent";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

const FeedbackPage = () => {
	const { data: organisation } = useOrganisation();
	const { t } = useTranslation();
	const query = useQuery();
	const {
		isOpen: isOpenFeedbackDetailDrawer,
		onOpen: onOpenFeedbackDetailDrawer,
		onClose: onCloseFeedbackDetailDrawer,
	} = useDisclosure();

	const { mutate: deleteRating } = useDeleteFeedback();

	const modalStore = useConfirmModalStore();

	const [selectedFilter, setSelectedFilter] = useQueryState("t", {
		initialValue: feedBackTypeTab[0].id,
	});
	const [selectedDateRange, setSelectedDateRange] = useQueryState<{
		startDate: Date;
		endDate: Date;
	}>("r", {
		initialValue: getStartEndFromQuery(query),
		valueFromString: (val) => {
			if (!val) return;
			const json = JSON.parse(val);
			json.startDate = new Date(json.startDate);
			json.endDate = new Date(json.endDate);
			return json;
		},
		valueToString: (val) => (val ? JSON.stringify(val) : ""),
	});
	const [consultantId, setConsultantId] = useQueryState<string>("consultant", {
		initialValue: "",
	});
	const [customerId, setCustomerId] = useQueryState<string>("customer", { initialValue: "" });
	const [selectedRate, setSelectedRate] = useState<FeedbackModal | null>(null);

	const isConsultantTab = selectedFilter === feedBackTypeTab[0].id;

	const {
		ratings: consultantRatings,
		isLoading: isLoadingConRating,
		refetch: refetchRatings,
	} = useConsultantRatings(
		dayjs(selectedDateRange.startDate).startOf("day").toISOString(),
		dayjs(selectedDateRange.endDate).endOf("day").toISOString(),
		[consultantId],
		["consultant", "customer", "job", "metadata.createdBy"],
		isConsultantTab
	);
	// TODO: need to fix this when BE is ready
	const { ratings: customerRatings, isLoading: isLoadingCusRating } = useCustomerRatings(
		dayjs(selectedDateRange.startDate).startOf("day").toISOString(),
		dayjs(selectedDateRange.endDate).endOf("day").toISOString(),
		[customerId],
		["consultant", "customer", "job"],
		!isConsultantTab
	);

	const isLoading = isLoadingConRating || isLoadingCusRating;
	const isEmptyLoadedData =
		!isLoading &&
		((!isConsultantTab && customerRatings && customerRatings.length === 0) ||
			(isConsultantTab && consultantRatings && consultantRatings.length === 0));

	const onDrawerToggle = (rateData?: ConsultantRating | CustomerRating) => {
		if (rateData) {
			if (isConsultantTab) {
				setSelectedRate(getFeedbackData(rateData, isConsultantTab));
			} else {
				// TODO: need to add logic in here
				// setSelectedRate(getFeedbackData(rateData,!isConsultantTab));
			}
			onOpenFeedbackDetailDrawer();
		} else {
			setSelectedRate(null);
			onCloseFeedbackDetailDrawer();
		}
	};

	function handleRatingDelete(consultantId: string, ratingId: string) {
		const deleteConfirmModal: ConfirmModalProps = {
			title: t("jobReportFeedback.deleteMessageTitle"),
			text: t("jobReportFeedback.deleteMessageText"),
			acceptButtonText: t("YES"),
			rejectButtonText: t("NO"),
			onAccept: () =>
				deleteRating({
					consultantId: consultantId,
					ratingId: ratingId,
					silent: false,
					callback: () => refetchRatings(),
				}),
			onReject: () => {},
			type: "WARNING",
		};
		modalStore.newModal(deleteConfirmModal);
	}

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={FeedbackReportBreadcrumbs} />
			<HStack alignItems={"center"} gap={5}>
				{feedBackTypeTab.map((f) => (
					<Tab
						key={f.id}
						action={() => setSelectedFilter(f.id)}
						text={f.label}
						role="filter"
						active={f.id === selectedFilter}
					/>
				))}
				<AppRangePicker
					margin={"15px 0 0 0"}
					placeholder={t("timeReports.selectDateRange")}
					value={
						selectedDateRange
							? {
									start: selectedDateRange.startDate,
									end: selectedDateRange.endDate,
							  }
							: undefined
					}
					onChange={(range?: DateRange) =>
						range &&
						setSelectedDateRange({
							startDate: range.start,
							endDate: range.end,
						})
					}
				/>
				{isConsultantTab ? (
					<ConsultantAutoComplete
						onChange={setConsultantId}
						value={consultantId}
						placeholder="schedule.filterConsultant"
						width={250}
						icon="filter"
					/>
				) : (
					<CustomerAutoComplete
						onChange={setCustomerId}
						value={customerId}
						placeholder="schedule.filterCustomer"
						width={250}
						icon="filter"
					/>
				)}
			</HStack>
			{isLoading ? (
				<LoadingIndicator />
			) : isEmptyLoadedData ? (
				<EmptyState margin="2rem 0" label="NO_FEEDBACKS" />
			) : (
				<TableLayout>
					<table>
						<thead>
							<tr>
								<th>{!isConsultantTab ? t("CUSTOMER") : t("CONSULTANT")} </th>
								<th>
									{!isConsultantTab
										? `${t("JOB")}/${t("CONSULTANT")}`
										: `${t("JOB")}/${t("CUSTOMER")}`}
								</th>
								<th>{t("SERVICE")}</th>
								<th>{t("SUBMITTED_BY")}</th>
								<th>{t("OPINION")}</th>
								<th>{t("COMMENT")}</th>
								{isConsultantTab && <th>{"Action"}</th>}
							</tr>
						</thead>
						<tbody>
							{isConsultantTab
								? consultantRatings?.map((data) => (
										<ConsultantFeedbackRow
											key={data.id}
											data={data}
											onRowClick={() => onDrawerToggle(data)}
											organization={organisation}
											deleteRate={() => handleRatingDelete(data.consultant.id, data.id)}
										/>
								  ))
								: customerRatings?.map((data, i) => (
										<CustomerFeedbackRow
											key={i}
											/* key={data.id} data={data} onRowClick={()=>onDrawerToggle(data)} organization={organisation} */
										/>
								  ))}
						</tbody>
					</table>
				</TableLayout>
			)}
			{selectedRate && (
				<Sheet
					open={isOpenFeedbackDetailDrawer}
					onOpenChange={(open) => {
						if (!open) onDrawerToggle();
					}}
				>
					<SheetContent
						side="right"
						className="w-full rounded-l-[15px] border-[#D4D4D4] sm:max-w-md"
						aria-describedby={undefined}
					>
						<SheetTitle className="sr-only">Feedback</SheetTitle>
						<div className="flex-1 overflow-y-auto p-5">
							<FeedbackContent selectedRate={selectedRate} />
						</div>
					</SheetContent>
				</Sheet>
			)}
		</div>
	);
};

export default FeedbackPage;
